import { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';
import { Check, Loader2, X } from 'lucide-react';

import { apiClient, type PendingSignup, type TempDefaults } from '@/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ApproveSignupDialog from '@/components/admin/ApproveSignupDialog';
import { pendingSignupsState } from '@/state/project';

type RowAction = 'approve' | 'deny';
type BusyMap = Record<string, RowAction | null>;

export default function PendingSignupsSection() {
  const [signups, setSignups] = useRecoilState(pendingSignupsState);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<BusyMap>({});
  const [approveTarget, setApproveTarget] = useState<PendingSignup | null>(null);
  const [tempDefaults, setTempDefaults] = useState<TempDefaults>({});
  const [denyTarget, setDenyTarget] = useState<PendingSignup | null>(null);
  const [denyReason, setDenyReason] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.listPendingSignups();
      setSignups(response.signups);
      setTempDefaults(response.temp_defaults ?? {});
    } catch (error) {
      console.error('Failed to load pending signups', error);
      toast.error('Could not load pending signup list');
    } finally {
      setLoading(false);
    }
  }, [setSignups]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setRowBusy = (id: string, action: RowAction | null) =>
    setBusy((prev) => ({ ...prev, [id]: action }));

  const handleApproved = (target: PendingSignup) => {
    setSignups((prev) => prev.filter((row) => row.id !== target.id));
  };

  const handleDenySubmit = async () => {
    if (!denyTarget) return;
    const reason = denyReason.trim();
    if (!reason) {
      toast.error('Deny reason is required');
      return;
    }
    setRowBusy(denyTarget.id, 'deny');
    try {
      await apiClient.denyPendingSignup(denyTarget.id, reason);
      setSignups((prev) => prev.filter((row) => row.id !== denyTarget.id));
      toast.success(`Denied ${denyTarget.email}`);
      setDenyTarget(null);
      setDenyReason('');
    } catch (error: any) {
      console.error('Deny failed', error);
      toast.error(error?.message || 'Deny failed');
    } finally {
      if (denyTarget) setRowBusy(denyTarget.id, null);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            Pending signups
          </h3>
          <Badge variant="secondary">{signups.length}</Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
        </Button>
      </header>

      {signups.length === 0 ? (
        <p className="rounded-md border border-dashed border-border/60 bg-muted/30 px-3 py-4 text-center text-sm text-muted-foreground">
          {loading ? 'Loading…' : 'No pending signups.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {signups.map((row) => {
            const rowBusy = busy[row.id] ?? null;
            return (
              <li
                key={row.id}
                className="flex flex-col gap-2 rounded-md border border-border/60 bg-background/40 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {row.email}
                    </div>
                    {row.organization && (
                      <div className="truncate text-xs text-muted-foreground">
                        {row.organization}
                      </div>
                    )}
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground/70">
                      {new Date(row.requested_at).toLocaleString()} ·{' '}
                      {row.provider}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Approve"
                      disabled={rowBusy !== null}
                      onClick={() => setApproveTarget(row)}
                    >
                      {rowBusy === 'approve' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Deny"
                      disabled={rowBusy !== null}
                      onClick={() => {
                        setDenyTarget(row);
                        setDenyReason('');
                      }}
                    >
                      <X className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
                {row.intended_use && (
                  <p className="text-xs leading-relaxed text-foreground/80">
                    {row.intended_use}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ApproveSignupDialog
        target={approveTarget}
        defaults={tempDefaults}
        onClose={() => setApproveTarget(null)}
        onApproved={handleApproved}
      />

      <AlertDialog
        open={denyTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDenyTarget(null);
            setDenyReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deny signup request</AlertDialogTitle>
            <AlertDialogDescription>
              {denyTarget ? (
                <>
                  Provide a short reason for denying{' '}
                  <span className="font-medium text-foreground">
                    {denyTarget.email}
                  </span>
                  . The reason is sent to the user and recorded in the audit
                  trail.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <textarea
            rows={4}
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            placeholder="Reason for denial"
            className="mt-2 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDenySubmit();
              }}
            >
              Deny
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
